import { motion } from "framer-motion";
import {
  ShieldCheck,
  EyeOff,
  Database,
  Ban,
  Lock,
  FlaskConical,
  MessageSquare,
  Bot,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PageHeader } from "@/components/ui/page-header";

const sections = [
  {
    icon: EyeOff,
    title: "Anonymous by Design",
    body: "Your financial records are stored alongside an opaque, randomly generated user ID — nothing more. No names, no email addresses, and no traceable PII (Personally Identifiable Information — things like your name, email, phone number, or address) exist anywhere in our database next to your financial data.",
  },
  {
    icon: MessageSquare,
    title: "Feedback Anonymity",
    body: "When you submit feedback, only your opaque user ID and the current page path are attached. No name, email, or any other identifying detail is included. Feedback cannot be traced back to you as an individual.",
  },
  {
    icon: Database,
    title: "What Data Is Collected",
    body: "We store the financial information you enter: transactions, account balances, budgets, savings goals, and your app settings. All of this is associated only with your opaque user ID — never with your real-world identity.",
  },
  {
    icon: Ban,
    title: "What Is NOT Collected",
    body: "We do not collect your name, email address, or any other traceable PII (Personally Identifiable Information) alongside your financial records. No external identity (social login profiles, analytics identifiers, etc.) is linked to your financial data.",
  },
  {
    icon: Lock,
    title: "Data Security",
    body: "Your data is stored in Supabase with row-level security enforced at the database layer — only your session can read or write your records. Your data is never sold, shared with third parties, or used for advertising.",
  },
  {
    icon: Bot,
    title: "AI Chat & Data Transmission",
    body: "When you use the AI chat or financial wellness features, a summary of your "
        + "recent financial data is sent to the AI model to generate a response. This "
        + "may include up to 20 recent transactions (type, amount, category, and date), "
        + "budget summaries, and goal progress percentages — never your name, account "
        + "numbers, or any personally identifiable information.",
  },
  {
    icon: FlaskConical,
    title: "Early Access Notice",
    body: "BudgeAI is in its first test cycle. Features and this privacy statement may evolve as the app matures. If material changes are made to how your data is handled, we will communicate those changes. Your feedback helps shape what we build next.",
  },
];

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <div className="container max-w-3xl py-10 px-4">
        <PageHeader
          icon={ShieldCheck}
          title="Data Privacy & Usage"
          description="How BudgeAI handles your financial data."
        >
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-amber-500/15 text-amber-500 border border-amber-500/30">
            <FlaskConical className="h-3.5 w-3.5" /> Early Access
          </span>
        </PageHeader>

        {/* Intro callout */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="rounded-xl border border-primary/20 bg-primary/5 px-5 py-4 mb-8 space-y-1"
        >
          <p className="text-sm font-medium text-foreground">Your data belongs to you.</p>
          <p className="text-sm text-muted-foreground">
            BudgeAI is built around a simple principle: we need just enough data to make the
            app useful, and nothing more. This page explains exactly what we collect, what we
            don't, and how it is protected.
          </p>
        </motion.div>

        {/* Section cards */}
        <div className="space-y-4 mb-10">
          {sections.map((section, i) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.15 + i * 0.07 }}
            >
              <Card className="bg-card/50 border-border/40">
                <CardHeader className="pb-2 pt-5">
                  <CardTitle className="flex items-center gap-2.5 text-base font-semibold">
                    <section.icon className="h-4 w-4 text-primary-text shrink-0" />
                    {section.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">{section.body}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <Separator className="mb-6" />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-muted-foreground">
          <span>Last reviewed: February 2026</span>
          <Link to="/app/feedback" className="text-primary-text hover:underline underline-offset-4">
            Share feedback about this page
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
